import { AbstractHistogram } from 'hdr-histogram-js'
import React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from '../app/context'
import Navigator from '../app/navigator'
import { HistogramView } from '../domain/HistogramView'
import { createHistoryCriteria, HistoryCriteria, historyCriteriaDescription, historyTrendCriteria } from '../domain/history/HistoryCriteria'
import { Trends } from '../domain/history/Trends'
import { Report } from '../domain/Report'
import { SerializedHistogram } from '../domain/SerializedHistogram'
import { TraceList } from '../domain/TraceSummary'
import Loading from '../helper/loading'
import { PortalContentNavigation } from '../helper/portal-content-navigation'
import { TimeExplorerComponent } from '../monitor/history/time_explorer'
import { HistogramComponent } from '../monitor/operation/histogram'
import ProfileTable from '../monitor/profile_table'
import { ApplicationState } from '../redux/ApplicationState'
import { fetchTimeExplorerReportFor, fetchTransactionReportsFor } from '../redux/V2/DailyReport/actions'
import {
    getArchivedTracesForTransaction,
    getHistoryTransactionHistogram,
    getHistoryTransactionReport,
    getHistoryTrendsForTransaction,
    getTimeExplorerReportFor,
} from '../redux/V2/DailyReport/DailyReportState'
import { Box, BoxBody, BoxHeader, BoxTitle } from '../ui/box'
import { Col, Row } from '../ui/grid'
import { UpgradeNecessary } from '../upgrade'
import { GraphStats } from './graph-stats'
import { rangeSelectorHistoryView } from './rangeSelectorHistoryView'
import { TooltipGraphComponent } from './tooltip-graph'
import TrendsComponent from './trends'

interface Props {
    tidewaysContext: TidewaysContext
    navigator: any
    criteria: HistoryCriteria
    transactionId: number
    reportTimeExplorer: Report<SerializedHistogram> | null
    trends: Trends | null
    report: Report<AbstractHistogram> | null
    histogram: HistogramView | null
    fetchTransactionReportsFor: (criteria: HistoryCriteria, transactionId: number) => void
    fetchTimeExplorerReportFor: (environment: string, service: string) => void
    archivedTraces: TraceList
}

export class TransactionHistoryComponent extends React.Component<Props> {
    public componentDidMount(): void {
        this.fetchTimeExplorerData()
        this.fetchData()
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        const granularityChanged = prevProps.criteria.granularity !== this.props.criteria.granularity
        const dateChanged = prevProps.criteria.date
            ? prevProps.criteria.date.format('YYYY-MM-DD') !== this.props.criteria.date.format('YYYY-MM-DD')
            : false
        const transactionChanged = prevProps.transactionId !== this.props.transactionId

        if (granularityChanged || dateChanged || transactionChanged) {
            this.fetchData()

            return
        }

        const serviceChanged = prevProps.criteria.service !== this.props.criteria.service
        const environmentChanged = prevProps.criteria.environment !== this.props.criteria.environment

        if (serviceChanged || environmentChanged) {
            this.fetchData()
            this.fetchTimeExplorerData()
        }
    }

    public render() {
        if (!this.props.tidewaysContext.application.license.historyTransactions) {
            return null
        }
        return (
            <Row>
                {this.props.reportTimeExplorer ? (
                    <PortalContentNavigation>
                        <TimeExplorerComponent
                            data={this.props.reportTimeExplorer}
                            loading={false}
                            tidewaysContext={this.props.tidewaysContext}
                            navigator={this.props.navigator}
                            daysOnScale={21}
                            dataFixedTimezone={this.props.tidewaysContext.application.timezone}
                            selector={rangeSelectorHistoryView(
                                this.props.tidewaysContext,
                                this.props.navigator,
                                this.props.criteria.granularity,
                                this.props.criteria.date,
                            )}
                        />
                    </PortalContentNavigation>
                ) : null}
                <Col className="monitor history-box">
                    <Box>
                        <BoxHeader>
                            {this.props.report ? (
                                <nav className="navbar navbar-toolbar">
                                    <div className="container-fluid">
                                        <BoxTitle>
                                            History <strong>{historyCriteriaDescription(this.props.criteria)}</strong>
                                            <br />
                                            <i className="text-muted">
                                                <strong>{this.props.report.transactionName || 'unknown'}</strong>
                                            </i>
                                        </BoxTitle>
                                        <GraphStats
                                            report={this.props.report}
                                            hasCrawlerRatio={this.props.tidewaysContext.hasFeature('crawler_ratio')}
                                        />
                                    </div>
                                </nav>
                            ) : (
                                <Loading />
                            )}
                        </BoxHeader>
                        <BoxBody>
                            {this.props.report ? (
                                <TooltipGraphComponent
                                    {...this.props}
                                    data={{ report: this.props.report, events: [] }}
                                    parameters={{}}
                                    dataFixedTimezone={this.props.tidewaysContext.application.timezone}
                                />
                            ) : null}
                        </BoxBody>
                    </Box>
                </Col>
                {this.props.trends ? (
                    <div className="col-lg-12">
                        <div className="box box-default">
                            <div className="box-header">
                                <h3 className="box-title">Trends</h3>
                            </div>
                            <TrendsComponent data={{ trends: this.props.trends }} />
                        </div>
                    </div>
                ) : null}
                {this.props.histogram ? (
                    <Col className="monitor">
                        <HistogramComponent loading={false} data={{ report: this.props.histogram }} />
                    </Col>
                ) : null}
                <div className="col-md-12">
                    <div className="box">
                        <div className="box-header">
                            <h3 className="box-title">Traces</h3>
                        </div>
                        {!this.props.tidewaysContext.application.license.historyTraces ? (
                            <UpgradeNecessary title={''} featureName={'History traces'} />
                        ) : this.props.archivedTraces ? (
                            <div className="box-body no-padding">
                                <ProfileTable profiles={this.props.archivedTraces} profileRoute="XhprofProfilerBundle.Profile.quick" />
                            </div>
                        ) : null}
                    </div>
                </div>
            </Row>
        )
    }

    private fetchData = () => {
        this.props.fetchTransactionReportsFor(this.props.criteria, this.props.transactionId)
        historyTrendCriteria(this.props.criteria).map(trendCriteria =>
            this.props.fetchTransactionReportsFor(trendCriteria, this.props.transactionId),
        )
    }

    private fetchTimeExplorerData = () => {
        const { service, environment } = this.props.tidewaysContext

        this.props.fetchTimeExplorerReportFor(environment, service)
    }
}

export default connect(
    (globalState: ApplicationState, props: Props) => {
        const criteria = createHistoryCriteria(globalState.tidewaysContext)
        const transactionId = parseInt(globalState.tidewaysContext.route.parameters.operation, 10)
        return {
            tidewaysContext: globalState.tidewaysContext,
            navigator: new Navigator(globalState.tidewaysContext.route),
            criteria,
            transactionId,
            reportTimeExplorer: getTimeExplorerReportFor(globalState, criteria.environment, criteria.service),
            trends: getHistoryTrendsForTransaction(globalState, criteria, transactionId),
            report: getHistoryTransactionReport(globalState, criteria, transactionId),
            histogram: getHistoryTransactionHistogram(globalState, criteria, transactionId),
            archivedTraces: getArchivedTracesForTransaction(globalState, criteria, transactionId),
        }
    },
    {
        fetchTimeExplorerReportFor,
        fetchTransactionReportsFor,
    },
)(TransactionHistoryComponent)
